import type { DienstTijden, PloegSoort } from './werkbon-recurring'
import { leesPauzeAfspraak } from './werkbon-pauze'

const PLOEG_SOORTEN: PloegSoort[] = ['DAG', 'VROEG', 'LAAT', 'NACHT']

interface RawDienst {
  type: PloegSoort
  startTime: string
  endTime: string
  breakMinutes?: number
  pauzeVerloning?: DienstTijden['pauzeVerloning']
  pauzeFacturatieMinuten?: number
}

function isPloegSoort(v: unknown): v is PloegSoort {
  return typeof v === 'string' && (PLOEG_SOORTEN as string[]).includes(v)
}

function parseDienst(raw: unknown): RawDienst | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (!isPloegSoort(d.type)) return null
  if (typeof d.startTime !== 'string' || typeof d.endTime !== 'string') return null
  // FO-473 - dezelfde tolerante lezing als de server-cascade: een onbruikbare
  // waarde telt als "niet ingevuld", nooit als 0.
  const afspraak = leesPauzeAfspraak(d)
  return {
    type: d.type,
    startTime: d.startTime,
    endTime: d.endTime,
    breakMinutes: afspraak.minuten ?? undefined,
    pauzeVerloning: afspraak.verloning ?? undefined,
    pauzeFacturatieMinuten: afspraak.facturatieMinuten ?? undefined,
  }
}

/**
 * Zet de `diensten`-JSON van een `ClientWorkSchedule`
 * (`{ type, startTime, endTime, breakMinutes }[]`) om naar de
 * `Record<PloegSoort, DienstTijden>` die `generateRecurringDates` verwacht, mét
 * pauze per ploeg. Zo is het werkschema de bron van de diensttijden (één
 * waarheid — CLAUDE.md principe 3/4) i.p.v. dat de planner ze opnieuw intypt.
 *
 * Niet-gedefinieerde ploegen worden gevuld met de eerste geldige dienst als
 * veilige filler — de rotatie verwijst alleen naar geplande ploegen, dus die
 * filler wordt normaal nooit daadwerkelijk geëmit; hij maakt de Record alleen
 * compleet. Geeft `null` als er geen enkele geldige dienst is, zodat de
 * caller kan terugvallen op expliciet meegegeven diensttijden.
 *
 * FO-423 (batch-56) — een ontbrekende `breakMinutes` levert `pauzeMinutes:
 * undefined` op, NIET 0. Vóór deze wijziging mapte een dienst zonder ingevulde
 * pauze op een harde 0, waardoor de hele pauze-cascade (klant-default,
 * systeemdefault per diensttype) werd overgeslagen: een 3-ploegendienst van
 * 14:30-23:00 werd 8,50 u i.p.v. 8,00 u, en dat liep door in facturatie én
 * verloning. `undefined` laat de cascade in `resolvePauze` z'n werk doen;
 * een expliciete `breakMinutes: 0` ("geen pauze") blijft gewoon 0.
 *
 * Rooster-werkschema-flexibiliteit (PO Q22): wanneer het rotatie-pattern tóch
 * een ploeg gebruikt die géén eigen dienst heeft, valt die ploeg terug op de
 * eerste dienst (filler) — géén harde 400-fout. Geef `opts.usedPloegen`
 * (`distinctPloegen(pattern)`) + `opts.onFallback` mee om zo'n terugval te laten
 * waarschuwen (audit/UI). Zonder `opts` blijft het gedrag exact als voorheen.
 */
export interface ClientWorkScheduleToDienstTijdenOptions {
  /** Ploegen die het rotatie-pattern daadwerkelijk gebruikt (`distinctPloegen`). */
  usedPloegen?: PloegSoort[]
  /** Aangeroepen per gebruikte ploeg die op de eerste dienst terugvalt (Q22). */
  onFallback?: (info: { ploeg: PloegSoort; fallbackType: PloegSoort }) => void
}

export function clientWorkScheduleToDienstTijden(
  diensten: unknown,
  opts?: ClientWorkScheduleToDienstTijdenOptions,
): Record<PloegSoort, DienstTijden> | null {
  if (!Array.isArray(diensten)) return null
  const parsed = diensten
    .map(parseDienst)
    .filter((d): d is RawDienst => d !== null)
  if (parsed.length === 0) return null

  const toTijden = (d: RawDienst): DienstTijden => ({
    start: d.startTime,
    eind: d.endTime,
    // FO-423: geen `?? 0` — ontbrekende pauze blijft ONBEKEND zodat de cascade
    // in `resolvePauze` (klant-default → systeemdefault) 'm bepaalt.
    ...(d.breakMinutes != null ? { pauzeMinutes: d.breakMinutes } : {}),
    // FO-473: de twee andere assen van de dienst-afspraak reizen mee; ook hier
    // betekent "ontbreekt" dat de cascade beslist.
    ...(d.pauzeVerloning != null ? { pauzeVerloning: d.pauzeVerloning } : {}),
    ...(d.pauzeFacturatieMinuten != null ? { pauzeFacturatieMinuten: d.pauzeFacturatieMinuten } : {}),
  })

  const filler = toTijden(parsed[0])
  const result = {} as Record<PloegSoort, DienstTijden>
  for (const soort of PLOEG_SOORTEN) {
    const match = parsed.find((d) => d.type === soort)
    result[soort] = match ? toTijden(match) : filler
  }

  // Q22 — waarschuw (géén throw) wanneer een ploeg die het rooster-pattern wél
  // gebruikt geen eigen dienst heeft en dus op de eerste dienst terugvalt.
  if (opts?.onFallback && opts.usedPloegen) {
    for (const ploeg of opts.usedPloegen) {
      if (!parsed.some((d) => d.type === ploeg)) {
        opts.onFallback({ ploeg, fallbackType: parsed[0].type })
      }
    }
  }
  return result
}
